import { useLocalStorage } from "@mantine/hooks";
import { omit } from "lodash";
import { useCallback, useMemo } from "react";

import { APP_VERSION } from "@/config";

import type { LocaleStorageData } from "@/types/storage";
import type { ListTableColumnSortBy } from "../types";

type ColumnSorterState<K = any> = {
  id?: K;
  sortBy?: ListTableColumnSortBy;
};

type ColumnSorterDefaultState<K = any> = ColumnSorterState<K> & {
  replaceLocalStorage?: boolean;
};

type ColumnSorterOptions<T = any, K = keyof T> = {
  data: T[];
  sortBy: (id: K, sortBy?: ListTableColumnSortBy) => (a: T, b: T) => number;
};

export function useColumnSorter<T = any, K = keyof T>(
  namespace: string,
  defaultState?: ColumnSorterDefaultState<K>,
  options?: ColumnSorterOptions<T, K>
) {
  const defaultStorage = omit(defaultState, ["replaceLocalStorage"]) as ColumnSorterDefaultState<K>;
  const [{ data: storage, version }, updateStorage] = useLocalStorage<
    LocaleStorageData<ColumnSorterState<K>>
  >({
    key: namespace,
    defaultValue: { data: defaultStorage, version: APP_VERSION },
    getInitialValueInEffect: false,
  });

  const state: ColumnSorterState<K> =
    defaultState?.replaceLocalStorage || version !== APP_VERSION
      ? defaultStorage
      : (storage ?? defaultStorage);

  const sortedData = useMemo(() => {
    if (options) {
      const { data, sortBy } = options;
      // If there is no active sort, return original data (unsorted)
      if (state.id == null || state.sortBy == null) return data;

      const sorted = [...data].sort(sortBy(state.id, state.sortBy));

      return sorted;
    }
    return [];
  }, [options?.data, state]);

  const getUpdateSort = useCallback((id: K) => {
    return (sortBy?: ListTableColumnSortBy) => {
      if (sortBy == null) {
        updateStorage({ data: {} as ColumnSorterState<K>, version: APP_VERSION });
      } else {
        updateStorage({ data: { id, sortBy }, version: APP_VERSION });
      }
    };
  }, []);

  const getSortById = (id: K) => {
    if (id === state.id) {
      return state.sortBy;
    }
    return undefined;
  };

  const sort: { id: K; sortBy: ListTableColumnSortBy } | undefined =
    state.id == null || state.sortBy == null
      ? undefined
      : { id: state.id as K, sortBy: state.sortBy as ListTableColumnSortBy };

  return {
    data: sortedData,
    getSortById,
    getUpdateSort,
    sort,
  };
}
